import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Link,
  InputAdornment,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { styled } from '@mui/material/styles';
import PersonIcon from '@mui/icons-material/Person';
import LockIcon from '@mui/icons-material/Lock';
import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  fetchSignInMethodsForEmail,
  sendPasswordResetEmail,
  onAuthStateChanged,
  signOut,
  User
} from 'firebase/auth';
import { auth, db } from '../firebase';
import { collection, query, where, getDocs, doc, updateDoc } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import config from '../config/environment';

// Custom styled components
const LoginContainer = styled(Container)({
  height: '100vh',
  width: '100vw',
  maxWidth: '100% !important',
  margin: 0,
  padding: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: '#FFFFFF',
});

const LoginPaper = styled(Paper)({
  padding: '2rem',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  backgroundColor: '#FFE0B2',
  borderRadius: '10px',
  maxWidth: '400px',
  width: '100%',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
});

const LoginButton = styled(Button)({
  backgroundColor: '#FF7043',
  color: 'white',
  '&:hover': {
    backgroundColor: '#F4511E',
  },
  padding: '0.75rem 2rem',
  marginTop: '1.5rem',
  borderRadius: '5px',
  textTransform: 'none',
  fontSize: '1rem',
});

const StyledTextField = styled(TextField)({
  marginBottom: '1.5rem',
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'white',
    '& fieldset': {
      borderColor: '#FF7043',
    },
    '&:hover fieldset': {
      borderColor: '#F4511E',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#F4511E',
    },
  },
  '& .MuiInputAdornment-root .MuiSvgIcon-root': {
    color: '#FF7043',
  },
});

const LogoImage = styled('img')({
  width: '200px',
  height: '80px',
  objectFit: 'contain',
  marginBottom: '1rem'
});

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, logout, checkAdminStatus } = useAuth();
  const [imageError, setImageError] = useState(false);

  // Listen for auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          const adminUsersRef = collection(db, 'admin_users');
          const q = query(adminUsersRef, where('email', '==', user.email));
          const querySnapshot = await getDocs(q);

          if (!querySnapshot.empty) {
            const adminUserData = querySnapshot.docs[0].data();
            if (adminUserData.password_update) {
              await updateDoc(doc(db, 'admin_users', querySnapshot.docs[0].id), {
                password_update: new Date().toISOString()
              });
            }
          }
        } catch (error) {
          console.error('Error updating password in Firestore:', error);
        }
      }
    });

    return () => unsubscribe();
  }, []);

  const handleResetPassword = async () => {
    if (!email) {
      setError('Please enter your email address first');
      return;
    }

    if (!validateEmail(email)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      console.log('Checking if email is registered as admin user:', email);
      console.log('Using Firebase project:', config.environment);
      
      // Check if the email exists in Firebase Auth first
      console.log('Checking if email exists in Firebase Auth:', email);
      const signInMethods = await fetchSignInMethodsForEmail(auth, email);
      
      if (signInMethods.length === 0) {
        console.log('Email not found in Firebase Auth - showing custom error');
        setError('The entered email ID is not registered as an admin user. Please contact your administrator for access.');
        setLoading(false);
        return;
      }

      console.log('Email found in Firebase Auth, proceeding with password reset');

      console.log('Email verified as admin user, sending password reset email to:', email);
      
      // Send password reset email only if user is confirmed as admin
      await sendPasswordResetEmail(auth, email, {
        url: window.location.origin + '/login',
        handleCodeInApp: false
      });
      
      console.log('Password reset email sent successfully');
      setShowResetDialog(true);
      setError('');
      
    } catch (resetError: any) {
      console.error('Password reset error:', resetError);
      
      // Handle specific Firebase Auth errors
      let errorMessage = 'Failed to reset password. Please try again.';
      
      switch (resetError.code) {
        case 'auth/user-not-found':
          errorMessage = 'No account found with this email address. Please contact your administrator.';
          break;
        case 'auth/invalid-email':
          errorMessage = 'Please enter a valid email address.';
          break;
        case 'auth/too-many-requests':
          errorMessage = 'Too many password reset attempts. Please try again later.';
          break;
        case 'auth/network-request-failed':
          errorMessage = 'Network error. Please check your internet connection and try again.';
          break;
        case 'permission-denied':
        case 'missing-permissions':
          errorMessage = 'Unable to verify email. Please contact your administrator.';
          break;
        default:
          errorMessage = resetError.message || 'Failed to reset password. Please try again.';
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);
    if (value && !validateEmail(value)) {
      setEmailError('Please enter a valid email address');
    } else {
      setEmailError('');
    }
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPassword(value);
    if (value && value.length < 8) {
      setPasswordError('Password must be at least 8 characters long');
    } else {
      setPasswordError('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Check for empty credentials
    if (!email.trim() || !password.trim()) {
      setError('Please enter your email and password.');
      setLoading(false);
      return;
    }

    if (!validateEmail(email)) {
      setEmailError('Please enter a valid email address');
      setLoading(false);
      return;
    }

    if (password.length < 8) {
      setPasswordError('Password must be at least 8 characters long');
      setLoading(false);
      return;
    }

    try {
      // Attempt to sign in directly
      console.log('Attempting login for email:', email);
      await signInWithEmailAndPassword(auth, email, password);
      
      // After successful authentication, check if user is an admin
      console.log('Authentication successful, checking admin status');
      const isAdmin = await checkAdminStatus(email);
      
      if (!isAdmin) {
        console.log('User is not an admin, signing out');
        await signOut(auth);
        setError('The entered email ID is not registered as an admin user. Please contact your administrator for access.');
        setLoading(false);
        return;
      }
      
      console.log('User is confirmed as admin, navigating to dashboard');
      navigate('/dashboard');
    } catch (error: any) {
      console.error('Login error:', error);
      
      if (error.code === 'auth/user-not-found') {
        setError('The entered email ID is not registered as an admin user. Please contact your administrator for access.');
      } else if (error.code === 'auth/invalid-credential' || error.code === 'auth/wrong-password') {
        setError('Invalid password. Please click "Forgot password?" to reset it.');
      } else {
        setError('Login failed. Please check your credentials and try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    setLogoutDialogOpen(true);
  };

  const handleLogoutConfirm = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Error logging out:', error);
    } finally {
      setLogoutDialogOpen(false);
    }
  };

  const handleLogoutCancel = () => {
    setLogoutDialogOpen(false);
  };

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      display: 'flex', 
      flexDirection: 'column',
      backgroundColor: '#f5f5f5'
    }}>
      <LoginContainer>
        <LoginPaper elevation={3}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 4 }}>
            <LogoImage src="/yantras-logo.png" alt="Yantras Logo" />
            <Typography
              component="h1"
              variant="h5"
              sx={{ 
                color: '#FF7043', 
                fontWeight: 'bold',
                textTransform: 'uppercase',
                letterSpacing: '2px',
                fontSize: '1.5rem'
              }}
            >
              Admin Panel
            </Typography>
          </Box>
          
          <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }} noValidate>
            {error && (
              <Typography color="error" align="center" sx={{ mb: 2 }}>
                {error}
              </Typography>
            )}
            
            <StyledTextField
              fullWidth
              id="email"
              placeholder="Email"
              name="email"
              autoComplete="email"
              value={email}
              onChange={handleEmailChange}
              disabled={loading}
              error={!!emailError}
              helperText={emailError}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon />
                  </InputAdornment>
                ),
              }}
            />
            
            <StyledTextField
              fullWidth
              name="password"
              type="password"
              id="password"
              placeholder="Password"
              autoComplete="current-password"
              value={password}
              onChange={handlePasswordChange}
              disabled={loading}
              error={!!passwordError}
              helperText={passwordError}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon />
                  </InputAdornment>
                ),
              }}
            />

            <LoginButton
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Login'
              )}
            </LoginButton>

            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Link
                component="button"
                type="button"
                sx={{
                  color: '#FF7043',
                  textDecoration: 'none',
                  fontSize: '0.875rem',
                  '&:hover': {
                    textDecoration: 'underline',
                  }
                }}
                onClick={handleResetPassword}
              >
                Forgot password?
              </Link>
            </Box>
          </Box>
        </LoginPaper>

        <Dialog
          open={showResetDialog}
          onClose={() => setShowResetDialog(false)}
        >
          <DialogTitle>Password Reset Email Sent</DialogTitle>
          <DialogContent>
            <Typography>
              A password reset link has been sent to <strong>{email}</strong>. Please check your inbox and follow the instructions to reset your password.
            </Typography>
            <Typography sx={{ mt: 2, fontSize: '0.9rem', color: '#666' }}>
              If you don't see the email, please check your spam folder.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowResetDialog(false)} sx={{ color: '#FF7043' }}>
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </LoginContainer>

      {/* Logout Confirmation Dialog */}
      <Dialog
        open={logoutDialogOpen}
        onClose={handleLogoutCancel}
        PaperProps={{
          sx: {
            borderRadius: '8px',
            padding: '16px',
            minWidth: '300px'
          }
        }}
      >
        <DialogTitle sx={{ 
          fontSize: '18px', 
          fontWeight: 'bold',
          color: '#424242',
          textAlign: 'center'
        }}>
          Confirm Logout
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center' }}>
          <Typography sx={{ color: '#424242' }}>
            Are you sure you want to logout?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ 
          justifyContent: 'center',
          gap: 2,
          padding: '16px'
        }}>
          <Button
            onClick={handleLogoutCancel}
            variant="outlined"
            sx={{
              color: '#424242',
              borderColor: '#424242',
              '&:hover': {
                borderColor: '#424242',
                backgroundColor: 'rgba(66, 66, 66, 0.04)'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleLogoutConfirm}
            variant="contained"
            sx={{
              backgroundColor: '#FF7043',
              color: '#ffffff',
              '&:hover': {
                backgroundColor: '#F4511E'
              }
            }}
          >
            Logout
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
} 